/**
 * `/profile` — the things you may change about yourself.
 *
 * Your **display name**, your **password**, and **where you are signed in**. Not
 * your email address: that is
 * the identifier you sign in with and an account-takeover path through any
 * future reset flow, so changing it is an administrative act under `user.update`
 * rather than a self-service one. The screen says that instead of silently
 * omitting the field.
 *
 * Changing the password requires the current one even though there is a live
 * session — that is what stops a borrowed unlocked browser becoming a permanent
 * takeover — and succeeding **ends every session, including this one**. So the
 * screen signs the user out afterwards rather than leaving them on a page whose
 * next request will 401.
 *
 * Below those, modules add their own preferences through the `profile.sections`
 * slot — the display currency is one — each in its own panel, so a module adds
 * a preference without this screen changing.
 *
 * The sessions list is the finer-grained version of that: a password change is
 * the blunt instrument for "someone else has my account", and this is the
 * scalpel for "I left myself signed in on a machine I no longer have". Ending
 * the current session lands in exactly the same place as a password change, and
 * is handled the same way.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { SessionDto } from '@forsys/shared';
import DevicesOutlinedIcon from '@mui/icons-material/DevicesOutlined';
import KeyIcon from '@mui/icons-material/Key';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AsyncContent } from '../components/AsyncContent';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EntityTable } from '../components/EntityTable';
import { ErrorState } from '../components/ErrorState';
import { Columns } from '../components/Columns';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { Timestamp } from '../components/Timestamp';
import { useAsync } from '../hooks/useAsync';
import { useSubmit } from '../hooks/useSubmit';
import { useAuth } from '../auth/useAuth';
import { Slot } from '../modules/Slot';
import {
  changeOwnPassword,
  listOwnSessions,
  revokeOwnSession,
  updateOwnProfile,
} from '../api/auth';

export default function ProfilePage() {
  const { t } = useTranslation(['auth', 'common']);
  const { user, isSuperadmin, impersonator, applyUser, signOut } = useAuth();
  // The server refuses an administrator the target's password and sessions;
  // the screen does not offer them.
  const impersonating = impersonator !== null;
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [saved, setSaved] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeated, setRepeated] = useState('');

  const save = useSubmit(async () => {
    const { user: updated } = await updateOwnProfile({ displayName: displayName.trim() });
    applyUser(updated);
    setSaved(true);
    return updated;
  });

  const change = useSubmit(async () => {
    await changeOwnPassword({ currentPassword, newPassword });
    // The session that made this request is gone with all the others. Staying
    // here would mean the next click failing with an unexplained 401.
    await signOut();
    navigate('/login', { replace: true });
    return true;
  });

  const sessions = useAsync(() => listOwnSessions(), [], { enabled: !impersonating });
  const [ending, setEnding] = useState<SessionDto | null>(null);

  const revoke = useSubmit(async () => {
    if (!ending) return undefined;

    const result = await revokeOwnSession(ending.id);
    setEnding(null);

    // Ending the session this page is using is the same situation a password
    // change leaves behind: the cookie is already gone, so there is nothing left
    // to reload.
    if (result.wasCurrent) {
      await signOut();
      navigate('/login', { replace: true });
      return result;
    }

    sessions.reload();
    return result;
  });

  const mismatch = repeated !== '' && newPassword !== repeated;

  return (
    <>
      <PageHeader title={t('auth:profile.title')} description={user?.email} />

      <Columns>
        <Panel
          icon={<PersonOutlinedIcon fontSize="small" />}
          title={t('auth:profile.details')}
          actions={isSuperadmin && <Chip size="small" color="secondary" label={t('common:superadmin.chip')} />}
        >

          <Stack spacing={2}>
            <TextField
              label={t('auth:profile.displayName')}
              value={displayName}
              onChange={(event) => {
                setDisplayName(event.target.value);
                setSaved(false);
              }}
              fullWidth
            />

            <TextField
              label={t('auth:profile.email')}
              value={user?.email ?? ''}
              disabled
              fullWidth
              helperText={t('auth:profile.emailHelp')}
            />

            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('auth:profile.lastSignIn')}
              </Typography>
              <Typography variant="body2">
                <Timestamp value={user?.lastLoginAt} />
              </Typography>
            </Box>

            {save.error && <ErrorState error={save.error} severity="warning" />}
            {saved && <Alert severity="success">{t('common:state.saved')}</Alert>}

            <Box>
              <Button
                variant="contained"
                disabled={
                  save.pending ||
                  displayName.trim() === '' ||
                  displayName.trim() === user?.displayName
                }
                onClick={() => void save.submit()}
              >
                {save.pending ? t('common:action.saving') : t('common:action.save')}
              </Button>
            </Box>
          </Stack>
        </Panel>

        {impersonating ? (
          <Alert severity="info" sx={{ alignSelf: 'flex-start' }}>
            {t('auth:profile.impersonating')}
          </Alert>
        ) : (
          <Panel
            icon={<KeyIcon fontSize="small" />}
            title={t('auth:profile.password.title')}
            sx={{ alignSelf: 'flex-start' }}
          >

            <Stack spacing={2}>
              <Alert severity="warning">{t('auth:profile.password.warning')}</Alert>

              <TextField
                label={t('auth:profile.password.current')}
                type="password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                autoComplete="current-password"
                helperText={t('auth:profile.password.currentHelp')}
                fullWidth
              />
              <TextField
                label={t('auth:profile.password.new')}
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                autoComplete="new-password"
                fullWidth
              />
              <TextField
                label={t('auth:profile.password.repeat')}
                type="password"
                value={repeated}
                onChange={(event) => setRepeated(event.target.value)}
                autoComplete="new-password"
                error={mismatch}
                helperText={mismatch ? t('auth:profile.password.mismatch') : undefined}
                fullWidth
              />

              {change.error && <ErrorState error={change.error} severity="warning" />}

              <Box>
                <Button
                  variant="contained"
                  color="warning"
                  disabled={
                    change.pending ||
                    currentPassword === '' ||
                    newPassword === '' ||
                    newPassword !== repeated
                  }
                  onClick={() => void change.submit()}
                >
                  {change.pending
                    ? t('auth:profile.password.submitting')
                    : t('auth:profile.password.submit')}
                </Button>
              </Box>
            </Stack>
          </Panel>
        )}
      </Columns>

      {/* Two panels to a row, like the ones above. With no module contributing
          the grid is empty, and `:has` drops its margin too. */}
      <Box sx={{ mt: 3, '&:has(> :empty)': { display: 'none' } }}>
        <Columns align="start">
          <Slot name="profile.sections" props={{}} />
        </Columns>
      </Box>

      {!impersonating && (
      <>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 4, mb: 0.5 }}>
          <DevicesOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="subtitle2">{t('auth:profile.sessions.title')}</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('auth:profile.sessions.description')}
        </Typography>

        <AsyncContent state={sessions} placeholder="spinner">
          {({ sessions: rows }) => (
            <EntityTable
              rows={rows}
              rowKey={(session) => session.id}
              actionsWidth={80}
              empty={{
                icon: <DevicesOutlinedIcon fontSize="inherit" />,
                title: t('auth:profile.sessions.emptyTitle'),
                description: t('auth:profile.sessions.emptyDescription'),
                dense: true,
              }}
              columns={[
                {
                  field: 'userAgent',
                  label: t('auth:profile.sessions.column.device'),
                  render: (session) => (
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {session.userAgent ?? t('auth:profile.sessions.unknownDevice')}
                      </Typography>
                      {session.isCurrent && (
                        <Chip size="small" color="primary" label={t('auth:profile.sessions.thisDevice')} />
                      )}
                    </Stack>
                  ),
                },
                {
                  field: 'ip',
                  label: t('auth:profile.sessions.column.ip'),
                  width: 140,
                  render: (session) => (
                    <Typography variant="caption" color="text.secondary">
                      {session.ip ?? t('common:state.empty')}
                    </Typography>
                  ),
                },
                {
                  field: 'lastSeenAt',
                  label: t('auth:profile.sessions.column.lastSeen'),
                  width: 150,
                  render: (session) => (
                    <Typography variant="caption" color="text.secondary">
                      <Timestamp value={session.lastSeenAt} />
                    </Typography>
                  ),
                },
                {
                  field: 'createdAt',
                  label: t('auth:profile.sessions.column.signedIn'),
                  width: 170,
                  render: (session) => (
                    <Typography variant="caption" color="text.secondary">
                      <Timestamp value={session.createdAt} absolute />
                    </Typography>
                  ),
                },
                {
                  field: 'expiresAt',
                  label: t('auth:profile.sessions.column.expires'),
                  width: 150,
                  render: (session) => (
                    <Typography variant="caption" color="text.secondary">
                      <Timestamp value={session.expiresAt} />
                    </Typography>
                  ),
                },
              ]}
              actions={(session) => [
                {
                  key: 'revoke',
                  label: t('auth:profile.sessions.revoke'),
                  icon: <LogoutOutlinedIcon fontSize="small" />,
                  tone: 'error',
                  disabled: revoke.pending,
                  onClick: () => setEnding(session),
                },
              ]}
            />
          )}
        </AsyncContent>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {t('auth:profile.sessions.lastSeenHelp')}
        </Typography>
      </>
      )}

      <ConfirmDialog
        open={ending !== null}
        tone="error"
        title={t(
          ending?.isCurrent
            ? 'auth:profile.sessions.revokeDialog.currentTitle'
            : 'auth:profile.sessions.revokeDialog.title',
        )}
        message={t(
          ending?.isCurrent
            ? 'auth:profile.sessions.revokeDialog.currentMessage'
            : 'auth:profile.sessions.revokeDialog.message',
        )}
        confirmLabel={t('auth:profile.sessions.revokeDialog.confirm')}
        pending={revoke.pending}
        error={revoke.error}
        onClose={() => setEnding(null)}
        onConfirm={() => void revoke.submit()}
      />
    </>
  );
}
